/** Canonical application routes shared by the shell and command system. */
export const APP_ROUTES = {
  home: '/',
  playlists: '/playlists',
  /** A shared class setlist opens at /setlist/:shareId (#325). */
  sharedSetlist: '/setlist',
  /** The pilot admin panel (#371) — RLS is the lock, the route is plain. */
  admin: '/admin',
  feedback: '/feedback',
  privacy: '/privacy',
  /** An invite link opens at /join/:token: sign in and you are in. */
  join: '/join',
  workbench: '/workbench',
  scoreStudio: '/workbench/score',
  learn: '/learn',
  learnStart: '/learn/start',
  guide: '/learn/guide',
  fourNoteVoicings: '/learn/voicings',
  drop2Intro: '/learn/voicings/drop-2',
  drop3Intro: '/learn/voicings/drop-3',
  studies: '/learn/studies',
  progressions: '/learn/practice/progressions',
  chordLibrary: '/chords',
  scaleHarmony: '/scale-harmony',
  topNoteFinder: '/learn/reference/top-note-finder',
  lab: '/learn/explore/lab',
  corpus: '/learn/explore/corpus',
  metronome: '/metronome',
  articleSegno: '/articles/dal-segno-machine',
} as const;

export const LEGACY_ROUTES = {
  /** The Tunes page, folded into the home page's search (Oct 2026). */
  tunes: '/tunes',
  sequence: '/sequence',
  score: '/score',
  drop2Intro: '/learn/drop-2',
  chapters: '/chapters',
  progressions: '/progressions',
  chordLibrary: '/learn/reference/chords',
  melody: '/melody',
  lab: '/lab',
  corpus: '/corpus',
} as const;

export const PRIMARY_NAV = [
  { id: 'workbench', label: 'Workbench', to: APP_ROUTES.workbench, end: false },
  { id: 'library', label: 'Chord library', to: APP_ROUTES.chordLibrary, end: true },
  { id: 'scale_harmony', label: 'Scale harmony', to: APP_ROUTES.scaleHarmony, end: true },
  { id: 'progressions', label: 'Progressions', to: APP_ROUTES.progressions, end: true },
  { id: 'metronome', label: 'Metronome', to: APP_ROUTES.metronome, end: true },
  { id: 'learn', label: 'Learn', to: APP_ROUTES.learn, end: false },
] as const;

/** Canonical destinations shown by the command palette. */
export const PALETTE_PAGES = [
  { id: 'tunes', label: 'Home', to: APP_ROUTES.home },
  { id: 'workbench', label: 'Workbench', to: APP_ROUTES.workbench },
  { id: 'playlists', label: 'Playlists', to: APP_ROUTES.playlists },
  { id: 'metronome', label: 'Metronome', to: APP_ROUTES.metronome },
  { id: 'chords', label: 'Chord library', to: APP_ROUTES.chordLibrary },
  { id: 'scale_harmony', label: 'Scale harmony', to: APP_ROUTES.scaleHarmony },
  { id: 'learn', label: 'Learn', to: APP_ROUTES.learn },
  { id: 'learn_start', label: 'Where to start', to: APP_ROUTES.learnStart },
  { id: 'guide', label: 'How the app works', to: APP_ROUTES.guide },
  {
    id: 'four_note_voicings',
    label: 'How four-note voicings work',
    to: APP_ROUTES.fourNoteVoicings,
  },
  { id: 'drop_2', label: 'Drop 2 voicings', to: APP_ROUTES.drop2Intro },
  { id: 'drop_3', label: 'Drop 3 voicings', to: APP_ROUTES.drop3Intro },
  { id: 'studies', label: 'Studies', to: APP_ROUTES.studies },
  { id: 'progressions', label: 'Progressions', to: APP_ROUTES.progressions },
  { id: 'top_note_finder', label: 'Top-note finder', to: APP_ROUTES.topNoteFinder },
  { id: 'score', label: 'Score Studio', to: APP_ROUTES.scoreStudio },
  { id: 'lab', label: 'Lab', to: APP_ROUTES.lab },
  { id: 'corpus', label: 'Corpus', to: APP_ROUTES.corpus },
] as const;

/** Keep older command clients useful while page URLs and names settle. */
export const LEGACY_COMMAND_PAGES = [
  { id: 'sequence', label: 'Workbench', to: APP_ROUTES.workbench },
  { id: 'library', label: 'Chord library', to: APP_ROUTES.chordLibrary },
  { id: 'melody', label: 'Top-note finder', to: APP_ROUTES.topNoteFinder },
] as const;

export function isWorkbenchRoute(pathname: string): boolean {
  const normalized = pathname.length > 1 ? pathname.replace(/\/+$/, '') : pathname;
  // '/' is the HOME page (recents shelf), not a workbench alias.
  return normalized === APP_ROUTES.workbench || normalized === LEGACY_ROUTES.sequence;
}
